/** biome-ignore-all lint/suspicious/noArrayIndexKey: this should be ok */
import {FC, ReactNode, Fragment} from 'react';

import {entries} from '@shared/lib/utils/map_utils';
import {keys} from '@shared/lib/utils/map_utils';
import {SupportedLanguage} from '@shared/models/language';

import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {translations} from '@mavismart_frontend/utils/translation';

export type TranslateKey = keyof typeof translations;

interface TranslateProps {
  value: TranslateKey;
  params?: Record<string, string | number | ReactNode>;
}
export const Translate: FC<TranslateProps> = props => {
  const account = useAccount();
  const noReactParams: Record<string, string | number> = {};
  const reactParams: Record<string, ReactNode> = {};
  Object.entries(props.params ?? {}).forEach(([key, val]) => {
    if (typeof val === 'string' || typeof val === 'number') {
      noReactParams[key] = val;
    } else {
      reactParams[key] = val;
    }
  });
  const midTranslation = translate(props.value, getUserLanguage(account), noReactParams);
  const parts = midTranslation.split(/(\{[^}]+\})/g).filter(part => part !== '');
  const finalParts = parts.map((part, index) => {
    if (part.startsWith('{') && part.endsWith('}')) {
      const key = part.slice(1, -1);
      return <Fragment key={`${key}_${index}`}>{reactParams[key] ?? part}</Fragment>;
    }
    return <Fragment key={`${part}_${index}`}>{part}</Fragment>;
  });
  return <>{finalParts}</>;
};

Translate.displayName = 'Translate';

export function translate(
  value: TranslateKey,
  language: SupportedLanguage,
  params?: Record<string, string | number>
): string {
  if (translations[value] === undefined) {
    throw new Error(`Translation key ${value} not found`);
  }
  let translation = translations[value][language];
  if (params) {
    entries(params).forEach(([key, val]) => {
      translation = translation.replace(`{${key}}`, String(val));
    });
  }
  return translation;
}

export function maybeTranslate(
  value: string,
  language: SupportedLanguage,
  params?: Record<string, string | number>
): string {
  const translationKey = findTranslationKey(value);
  if (translationKey) {
    return translate(translationKey, language, params);
  }
  return value;
}

export function findTranslationKey(value: string | undefined): TranslateKey | undefined;
export function findTranslationKey(
  value: string | undefined,
  defaultValue: TranslateKey
): TranslateKey;
export function findTranslationKey(
  value: string | undefined,
  defaultValue?: TranslateKey
): TranslateKey | undefined {
  return keys(translations).find(key => key === value) ?? defaultValue;
}
